import { useState, useEffect, useCallback, useMemo } from 'react';
import { customerApi } from '../../../../api/customerApi';
import { useToast } from '../../../../context/ToastContext';

/**
 * Hook for customer management logic.
 * Handles fetching, filtering, searching, and CRUD operations.
 */
export const useCustomers = () => {
    const { showToast } = useToast();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'aktiv', 'inaktiv'
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'tile'
    
    // Selection for bulk actions
    const [selectedIds, setSelectedIds] = useState([]);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCustomer, setCurrentCustomer] = useState(null);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'

    /**
     * Load all customers from the API.
     */
    const loadCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await customerApi.getAll();
            setCustomers(data);
        } catch (err) {
            console.error('Failed to load customers:', err);
            showToast('Fehler beim Laden der Kunden', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Restore Customer Modal State if returning from deep link
    useEffect(() => {
        const backupStr = sessionStorage.getItem('customerModalBackup');
        if (backupStr && customers.length > 0) {
            try {
                const backup = JSON.parse(backupStr);
                sessionStorage.removeItem('customerModalBackup');
                if (backup.isOpen && backup.customerId) {
                    const targetCustomer = customers.find(c => String(c.id) === String(backup.customerId));
                    if (targetCustomer) {
                        setCurrentCustomer(targetCustomer);
                        setModalMode(backup.currentMode || 'edit');
                        setIsModalOpen(true);
                        
                        // Pass activeTab to CustomerModal using sessionStorage
                        sessionStorage.setItem('customerModalActiveTab', backup.activeTab || 'projects');
                    }
                }
            } catch (e) {
                console.error('Failed to restore customer modal backup:', e);
            }
        }
    }, [customers]);

    /**
     * Filtered and searched customers list.
     */
    const filteredCustomers = useMemo(() => {
        return customers.filter(customer => {
            const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
            const matchesSearch = !searchTerm || 
                customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.customer_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.address?.toLowerCase().includes(searchTerm.toLowerCase());
            
            return matchesStatus && matchesSearch;
        });
    }, [customers, statusFilter, searchTerm]);

    /**
     * Open modal for creating a new customer.
     */
    const openCreate = () => {
        setCurrentCustomer({
            name: '',
            customer_number: '',
            address: '',
            contact_person: '',
            email: '',
            phone: '',
            status: 'aktiv'
        });
        setModalMode('create');
        setIsModalOpen(true);
    };

    /**
     * Open modal for editing/viewing a customer.
     */
    const openEdit = (customer) => {
        setCurrentCustomer({ ...customer });
        setModalMode('edit');
        setIsModalOpen(true);
    };

    /**
     * Save customer (create or update).
     */
    const handleSave = async (customerData) => {
        try {
            if (modalMode === 'create') {
                await customerApi.create(customerData);
                showToast('Kunde erfolgreich erstellt', 'success');
            } else {
                await customerApi.update(customerData.id, customerData);
                showToast('Kunde erfolgreich aktualisiert', 'success');
            }
            setIsModalOpen(false);
            loadCustomers();
        } catch (err) {
            console.error('Failed to save customer:', err);
            showToast(err.response?.data?.error || 'Fehler beim Speichern', 'error');
        }
    };

    /**
     * Delete a single customer.
     */
    const handleDelete = async (id) => {
        if (!window.confirm('Möchten Sie diesen Kunden wirklich löschen?')) return;
        try {
            await customerApi.delete(id);
            showToast('Kunde gelöscht', 'success');
            loadCustomers();
        } catch (err) {
            console.error('Failed to delete customer:', err);
            showToast('Fehler beim Löschen', 'error');
        }
    };

    /**
     * Bulk status update.
     */
    const handleBulkStatus = async (newStatus) => {
        if (selectedIds.length === 0) return;
        try {
            await customerApi.bulkUpdateStatus(selectedIds, newStatus);
            showToast(`${selectedIds.length} Kunden aktualisiert`, 'success');
            setSelectedIds([]);
            loadCustomers();
        } catch (err) {
            showToast('Fehler beim Bulk-Update', 'error');
        }
    };

    /**
     * Bulk delete.
     */
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0 || !window.confirm(`${selectedIds.length} Kunden wirklich löschen?`)) return;
        try {
            await customerApi.bulkDelete(selectedIds);
            showToast(`${selectedIds.length} Kunden gelöscht`, 'success');
            setSelectedIds([]);
            loadCustomers();
        } catch (err) {
            showToast('Fehler beim Löschen', 'error');
        }
    };

    return {
        customers: filteredCustomers,
        allCustomers: customers,
        loading,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        viewMode,
        setViewMode,
        selectedIds,
        setSelectedIds,
        isModalOpen,
        setIsModalOpen,
        currentCustomer,
        modalMode,
        openCreate,
        openEdit,
        handleSave,
        handleDelete,
        handleBulkStatus,
        handleBulkDelete,
        refresh: loadCustomers
    };
};
